import React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  ListSubheader,
  Divider,
  TextField,
  InputAdornment,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useUsers } from "../hooks/useUsers";
import useDebouncedValue from "../hooks/useDebouncedValue";

/** ura === 1 indica usuário ChatBot (bot de atendimento) */
const isChatBotUser = (user) => Number(user?.ura) === 1;

/** Workspaces com milhares de usuários: nunca renderiza a lista inteira. */
const MAX_VISIBLE_USERS = 200;
/** Resultados de busca ficam curtos para incentivar o refinamento do termo. */
const MAX_SEARCH_RESULTS = 50;
/** A partir de quantos caracteres a busca também vai ao backend. */
const REMOTE_SEARCH_MIN_CHARS = 2;

const normalizeText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const onlyDigits = (value) => String(value ?? "").replace(/\D+/g, "");

const getUserName = (user) => user?.nome ?? user?.name ?? "";
const getUserEmail = (user) => user?.email ?? "";
const getUserPhone = (user) => user?.celular ?? user?.telefone ?? "";
const getUserRole = (user) => user?.cargo_nome ?? user?.cargo ?? "";

const matchesUser = (user, term) => {
  const digits = onlyDigits(term);
  if (digits && onlyDigits(getUserPhone(user)).includes(digits)) return true;

  const haystack = normalizeText(
    [
      getUserName(user),
      getUserEmail(user),
      getUserPhone(user),
      getUserRole(user),
      user?.id_interno,
    ].join(" ")
  );
  return haystack.includes(normalizeText(term));
};

const matchesAgent = (agent, term) => {
  const haystack = normalizeText(
    [agent?.name, agent?.nome, agent?.descricao, agent?.description].join(" ")
  );
  return haystack.includes(normalizeText(term));
};

const dedupeUsersById = (list) => {
  const seen = new Set();
  return list.filter((user) => {
    const key = String(user?.id ?? "");
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const byName = (a, b) =>
  getUserName(a).localeCompare(getUserName(b), "pt-BR", { sensitivity: "base" });

/**
 * Seletor unificado de atribuição:
 * - Fila (sem usuário / sem agente)
 * - Usuários (humanos)
 * - ChatBots (usuários com ura = 1)
 * - Agentes de IA
 *
 * Busca por nome, e-mail, telefone ou cargo: filtra a lista já carregada e,
 * a partir de 2 caracteres, consulta o backend para alcançar usuários fora da página atual.
 *
 * value: { type: "queue" | "user" | "agent"; id: string|number|null; label: string; isChatBot?: boolean }
 */
const AssigneeSelector = ({
  value,
  onChange,
  users = [],
  agents = [],
  loading = false,
  size = "small",
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [search, setSearch] = React.useState("");

  const open = Boolean(anchorEl);
  const debouncedSearch = useDebouncedValue(search, 300);
  // Limpar o campo (ou fechar o menu) restaura a lista na hora, sem esperar o debounce.
  const term = search.trim() ? debouncedSearch.trim() : "";
  const remoteEnabled = open && term.length >= REMOTE_SEARCH_MIN_CHARS;

  const { data: remoteResp, isFetching: remoteLoading } = useUsers(
    1,
    MAX_SEARCH_RESULTS,
    term,
    "todos",
    { enabled: remoteEnabled, order: "nome" }
  );

  const remoteUsers = React.useMemo(
    () => (remoteEnabled ? remoteResp?.users ?? [] : []),
    [remoteEnabled, remoteResp]
  );

  // Só processa as listas com o menu aberto: a tela de conversas monta um seletor por linha.
  const { humanUsers, chatbotUsers, filteredAgents } = React.useMemo(() => {
    if (!open) return { humanUsers: [], chatbotUsers: [], filteredAgents: [] };

    const base = users || [];
    const searched = term
      ? dedupeUsersById([
          ...base.filter((user) => matchesUser(user, term)),
          ...remoteUsers,
        ]).sort(byName)
      : base;

    return {
      humanUsers: searched.filter((user) => !isChatBotUser(user)),
      chatbotUsers: searched.filter(isChatBotUser),
      filteredAgents: term
        ? (agents || []).filter((agent) => matchesAgent(agent, term))
        : agents || [],
    };
  }, [open, users, agents, remoteUsers, term]);

  const visibleLimit = term ? MAX_SEARCH_RESULTS : MAX_VISIBLE_USERS;
  const visibleHumans = humanUsers.slice(0, visibleLimit);
  const visibleChatbots = chatbotUsers.slice(0, visibleLimit);
  const hiddenCount =
    humanUsers.length -
    visibleHumans.length +
    (chatbotUsers.length - visibleChatbots.length);

  const isSearching = remoteEnabled && remoteLoading;
  const isLoadingUsers = Boolean(loading) && !term;
  const showQueueOption =
    !term || "fila sem atribuicao".includes(normalizeText(term));
  const hasResults =
    showQueueOption ||
    visibleHumans.length > 0 ||
    visibleChatbots.length > 0 ||
    filteredAgents.length > 0 ||
    isLoadingUsers;

  const showAgentsDivider = filteredAgents.length > 0 && showQueueOption;
  const showUsersDivider =
    (visibleHumans.length > 0 || isLoadingUsers) &&
    (showQueueOption || filteredAgents.length > 0);
  const showChatbotsDivider =
    visibleChatbots.length > 0 &&
    (showQueueOption || filteredAgents.length > 0 || visibleHumans.length > 0);

  const currentType = value?.type || "queue";
  const currentLabel = value?.label || "Fila";
  const currentIsChatBot = value?.isChatBot === true;

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const handleSelect = (next) => {
    onChange && onChange(next);
    handleClose();
  };

  const getAgentId = (agent) => {
    if (!agent || typeof agent !== "object") return null;
    if (agent.id != null) return agent.id;
    if (agent.id_agente != null) return agent.id_agente;
    if (agent.agentId != null) return agent.agentId;

    // Fallback: pega o primeiro campo numérico cujo nome contenha "id"
    const dynamicKey = Object.keys(agent).find((key) => {
      const lower = key.toLowerCase();
      return (
        lower.includes("id") &&
        agent[key] !== null &&
        agent[key] !== undefined &&
        (typeof agent[key] === "number" || !Number.isNaN(Number(agent[key])))
      );
    });

    return dynamicKey ? agent[dynamicKey] : null;
  };

  const getChipStyles = () => {
    if (currentType === "agent") {
      return {
        bg: theme.palette.secondary.main,
        color: theme.palette.common.white,
      };
    }
    if (currentType === "user") {
      if (currentIsChatBot) {
        return {
          bg: theme.palette.warning?.main || "#ed6c02",
          color: theme.palette.common.white,
        };
      }
      // Diferenciação visual:
      // - "Minha Solicitação" -> destaque em verde (sucesso)
      // - Outro usuário        -> tom azul padrão
      const isMine =
        typeof currentLabel === "string" &&
        currentLabel.toLowerCase().includes("minha solicitação");

      if (isMine) {
        return {
          bg: theme.palette.success.main,
          color: theme.palette.common.white,
        };
      }

      return {
        bg: theme.palette.info.main,
        color: theme.palette.common.white,
      };
    }

    const isNoUser =
      typeof currentLabel === "string" &&
      currentLabel.toLowerCase().includes("sem usuário");

    if (isNoUser) {
      const bg = theme.palette.error.light || "#ef9a9a";
      return {
        bg,
        color: theme.palette.getContrastText
          ? theme.palette.getContrastText(bg)
          : theme.palette.text.primary,
      };
    }

    return {
      bg: theme.palette.grey[800],
      color: theme.palette.common.white,
    };
  };

  const { bg, color } = getChipStyles();

  const displayLabel =
    currentType === "agent"
      ? currentLabel || "Agente de IA"
      : currentType === "user"
      ? currentIsChatBot
        ? (currentLabel && !String(currentLabel).endsWith("(ChatBot)")
          ? `${currentLabel} (ChatBot)`
          : currentLabel || "ChatBot")
        : currentLabel || "Usuário"
      : currentLabel || "Fila";

  const renderUserDetails = (user) => {
    const email = String(getUserEmail(user) ?? "").trim();
    const phone = String(getUserPhone(user) ?? "").trim();
    const role = String(getUserRole(user) ?? "").trim();
    if (!email && !phone && !role) return null;

    return (
      <Box
        sx={{
          mt: 0.25,
          display: "flex",
          flexDirection: "column",
          gap: 0.15,
          minWidth: 0,
        }}
      >
        {email ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", whiteSpace: "normal", overflowWrap: "anywhere" }}
          >
            {email}
          </Typography>
        ) : null}
        {phone || role ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", whiteSpace: "normal", overflowWrap: "anywhere" }}
          >
            {[phone, role].filter(Boolean).join(" · ")}
          </Typography>
        ) : null}
      </Box>
    );
  };

  return (
    <>
      <Tooltip title="Alterar responsável (fila / usuário / ChatBot / agente de IA)">
        <Chip
          label={displayLabel}
          size={size}
          onClick={handleOpen}
          sx={{
            backgroundColor: bg,
            color,
            fontSize: "0.75rem",
            height: size === "small" ? "24px" : "28px",
            cursor: "pointer",
            maxWidth: "220px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        />
      </Tooltip>

      <Menu
        id="assignee-selector-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        autoFocus={false}
        disableAutoFocusItem
        MenuListProps={{ "aria-labelledby": "assignee-chip-selector" }}
        PaperProps={{
          sx: {
            minWidth: 360,
            width: 440,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: 520,
          },
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            position: "sticky",
            top: 0,
            zIndex: 2,
            backgroundColor: theme.palette.background.paper,
          }}
          onKeyDown={(event) => {
            if (event.key !== "Escape" && event.key !== "Tab") {
              event.stopPropagation();
            }
          }}
        >
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nome, e-mail, telefone ou cargo"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: isSearching ? (
                <InputAdornment position="end">
                  <CircularProgress color="inherit" size={14} />
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        {showQueueOption && (
          <MenuItem
            selected={currentType === "queue"}
            onClick={() =>
              handleSelect({
                type: "queue",
                id: null,
                label: "Fila",
              })
            }
          >
            Fila (sem atribuição)
          </MenuItem>
        )}

        {showAgentsDivider && <Divider />}
        {filteredAgents.length > 0 && (
          <ListSubheader disableSticky>Agentes de IA</ListSubheader>
        )}
        {filteredAgents.map((agent) => {
          const aid = getAgentId(agent);
          return (
            <MenuItem
              key={`agent-${aid ?? "sem-id"}`}
              selected={
                currentType === "agent" &&
                String(value?.id ?? "") === String(aid ?? "")
              }
              onClick={() =>
                handleSelect({
                  type: "agent",
                  id: aid,
                  label:
                    agent.name ??
                    agent.nome ??
                    `Agente #${aid ?? "desconhecido"}`,
                  raw: agent,
                })
              }
              sx={{
                fontWeight: "bold",
                color: theme.palette.secondary.main,
              }}
            >
              {agent.name ?? agent.nome ?? `Agente #${aid ?? "sem-id"}`}
            </MenuItem>
          );
        })}

        {showUsersDivider && <Divider />}
        {isLoadingUsers && visibleHumans.length === 0 && (
          <MenuItem disabled>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress color="inherit" size={14} />
              <Typography variant="body2" color="text.secondary">
                Carregando usuários...
              </Typography>
            </Box>
          </MenuItem>
        )}
        {visibleHumans.length > 0 && (
          <ListSubheader disableSticky>Usuários</ListSubheader>
        )}
        {visibleHumans.map((user) => (
          <MenuItem
            key={`user-${user.id}`}
            selected={
              currentType === "user" &&
              !value?.isChatBot &&
              String(value?.id ?? "") === String(user.id ?? "")
            }
            onClick={() =>
              handleSelect({
                type: "user",
                id: user.id,
                label: getUserName(user) || `Usuário #${user.id}`,
                isChatBot: false,
              })
            }
            sx={{
              display: "block",
              py: 0.9,
              whiteSpace: "normal",
              alignItems: "flex-start",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                display: "block",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                fontWeight: 500,
              }}
            >
              {getUserName(user) || `Usuário #${user.id}`}
            </Typography>
            {renderUserDetails(user)}
          </MenuItem>
        ))}

        {showChatbotsDivider && <Divider />}
        {visibleChatbots.length > 0 && (
          <ListSubheader disableSticky>ChatBots</ListSubheader>
        )}
        {visibleChatbots.map((user) => (
          <MenuItem
            key={`chatbot-${user.id}`}
            selected={
              currentType === "user" &&
              value?.isChatBot &&
              String(value?.id ?? "") === String(user.id ?? "")
            }
            onClick={() =>
              handleSelect({
                type: "user",
                id: user.id,
                label: getUserName(user) || `ChatBot #${user.id}`,
                isChatBot: true,
                raw: user,
              })
            }
            sx={{
              color: theme.palette.warning?.main || "#ed6c02",
              fontWeight: 500,
            }}
          >
            {getUserName(user) || `ChatBot #${user.id}`} (ChatBot)
          </MenuItem>
        ))}

        {hiddenCount > 0 && (
          <ListSubheader disableSticky sx={{ lineHeight: 1.6, py: 1 }}>
            <Typography variant="caption" color="text.secondary">
              +{hiddenCount} resultado(s) não exibido(s). Refine a busca.
            </Typography>
          </ListSubheader>
        )}

        {!hasResults && !isSearching && !isLoadingUsers && (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Nenhum responsável encontrado
            </Typography>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default AssigneeSelector;
